/**
 * ★ Fase 48 — Date Verification Pass (Funnel IT-AL) gestibili dal CRM.
 * Gli admin aggiornano posti/disponibilità SENZA modifiche al codice:
 * la landing /f/it-al e il chatbot leggono questi dati live.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { CalendarDays, Minus, Plus, Save, Star, ExternalLink } from 'lucide-react';

function SeatStepper({ value, max, onChange, testId }) {
  return (
    <div className="flex items-center gap-1.5">
      <Button type="button" size="icon" variant="outline" className="h-8 w-8" onClick={() => onChange(Math.max(0, value - 1))} data-testid={`${testId}-minus`}>
        <Minus className="h-3.5 w-3.5" />
      </Button>
      <Input
        type="number" min={0} max={max} value={value}
        onChange={(e) => onChange(Math.max(0, Math.min(max, Number(e.target.value) || 0)))}
        className="h-8 w-16 text-center"
        data-testid={`${testId}-input`}
      />
      <Button type="button" size="icon" variant="outline" className="h-8 w-8" onClick={() => onChange(Math.min(max, value + 1))} data-testid={`${testId}-plus`}>
        <Plus className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export default function FunnelDatesPage() {
  // ★ Fase 52 — stessa landing, tre paesi: posti gestibili per ognuno
  const FUNNELS = {
    'IT-AL': { label: 'Albania', title: 'Weekend · €300 a persona', desc: '24 posti totali per data. Imposta i posti ancora disponibili.' },
    'IT-ID': { label: 'Indonesia', title: '9 giorni / 7 notti · €5.000 a persona', desc: '12 posti totali per data (una partenza ogni 14 giorni). Imposta i posti disponibili.' },
    'IT-TH': { label: 'Thailandia', title: '9 giorni / 7 notti · €3.500 a persona', desc: 'Attualmente SOLD OUT: alza i posti disponibili per riaprire le vendite.' },
  };
  const [funnel, setFunnel] = useState('IT-AL');
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState({});

  const load = useCallback(() => {
    setRows(null);
    api.get('/funnel-dates', { params: { funnel } })
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error('Impossibile caricare le date.'));
  }, [funnel]);

  useEffect(() => { load(); }, [load]);

  const patchLocal = (id, patch) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const save = async (row) => {
    setSaving((s) => ({ ...s, [row.id]: true }));
    try {
      const payload = row.kind === 'weekend'
        ? { left: row.left, total: row.total, is_active: row.is_active !== false }
        : { max_people: row.max_people, is_active: row.is_active !== false };
      const r = await api.put(`/funnel-dates/${row.id}`, payload);
      patchLocal(row.id, r.data || {});
      toast.success(`Salvato: ${row.label}`, { description: 'La landing e il chatbot sono già aggiornati.' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Salvataggio non riuscito.');
    } finally {
      setSaving((s) => ({ ...s, [row.id]: false }));
    }
  };

  const weekend = (rows || []).filter((r) => r.kind === 'weekend');
  const extra = (rows || []).filter((r) => r.kind === 'extra');

  return (
    <div className="space-y-6" data-testid="funnel-dates-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Date Verification Pass</h1>
          <p className="text-sm text-muted-foreground">
            Funnel IT (/f/it): i posti aggiornati qui compaiono subito sulla landing e nel chatbot.
          </p>
        </div>
        <a href="/f/it" target="_blank" rel="noopener noreferrer" data-testid="funnel-dates-open-landing">
          <Button variant="outline" className="gap-2"><ExternalLink className="h-4 w-4" /> Apri la landing</Button>
        </a>
      </div>

      {/* ★ Fase 52 — selettore paese */}
      <div className="flex flex-wrap gap-2" data-testid="funnel-dates-tabs">
        {Object.entries(FUNNELS).map(([code, meta]) => (
          <Button
            key={code}
            variant={funnel === code ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFunnel(code)}
            data-testid={`funnel-dates-tab-${code.toLowerCase()}`}
          >
            {meta.label}
          </Button>
        ))}
      </div>

      <Card data-testid="funnel-dates-weekend-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <CalendarDays className="h-5 w-5" /> {FUNNELS[funnel].label} · {FUNNELS[funnel].title}
          </CardTitle>
          <CardDescription>{FUNNELS[funnel].desc}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!rows && [...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
          {weekend.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center gap-3 rounded-lg border p-3" data-testid={`funnel-date-row-${d.id}`}>
              <div className="min-w-[160px]">
                <div className="font-semibold">{d.label}</div>
                <div className="text-xs text-muted-foreground">{d.start}</div>
              </div>
              <Badge variant={d.left <= 0 ? 'destructive' : d.left <= 5 ? 'secondary' : 'outline'} data-testid={`funnel-date-badge-${d.id}`}>
                {d.left <= 0 ? 'Sold out' : `${d.left} / ${d.total} disponibili`}
              </Badge>
              <div className="ml-auto flex items-center gap-4">
                <SeatStepper value={d.left ?? 0} max={d.total ?? 24} onChange={(v) => patchLocal(d.id, { left: v })} testId={`funnel-date-left-${d.id}`} />
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  Attiva
                  <Switch checked={d.is_active !== false} onCheckedChange={(v) => patchLocal(d.id, { is_active: v })} data-testid={`funnel-date-active-${d.id}`} />
                </div>
                <Button size="sm" className="gap-1.5" disabled={!!saving[d.id]} onClick={() => save(d)} data-testid={`funnel-date-save-${d.id}`}>
                  <Save className="h-3.5 w-3.5" /> Salva
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {extra.length > 0 && (
      <Card data-testid="funnel-dates-extra-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Star className="h-5 w-5" /> Extra Pass Private · €500 infrasettimanale
          </CardTitle>
          <CardDescription>Gruppi ristretti: imposta il numero massimo di investitori per data.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!rows && [...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
          {extra.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center gap-3 rounded-lg border p-3" data-testid={`funnel-date-row-${d.id}`}>
              <div className="min-w-[160px]">
                <div className="font-semibold">{d.label}</div>
                <div className="text-xs text-muted-foreground">{d.start}</div>
              </div>
              <Badge variant="outline">max {d.max_people ?? 5} investitori</Badge>
              <div className="ml-auto flex items-center gap-4">
                <SeatStepper value={d.max_people ?? 5} max={24} onChange={(v) => patchLocal(d.id, { max_people: v })} testId={`funnel-date-max-${d.id}`} />
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  Attiva
                  <Switch checked={d.is_active !== false} onCheckedChange={(v) => patchLocal(d.id, { is_active: v })} data-testid={`funnel-date-active-${d.id}`} />
                </div>
                <Button size="sm" className="gap-1.5" disabled={!!saving[d.id]} onClick={() => save(d)} data-testid={`funnel-date-save-${d.id}`}>
                  <Save className="h-3.5 w-3.5" /> Salva
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
